import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  ChevronLeft,
  ChevronRight,
  Download,
  ExternalLink,
  ImageOff,
  Link as LinkIcon,
  Share2,
  X,
} from "lucide-react";
import "./pr-kit.css";
import "./page-banner.css";

const imageDetails = [
  ["01", "Portraits"], ["02", "Portraits"], ["03", "Portraits"], ["04", "Portraits"],
  ["05", "Portraits"], ["06", "Portraits"], ["07", "Portraits"], ["08", "Portraits"],
  ["09", "Portraits"], ["10", "Portraits"], ["11", "Portraits"], ["12", "Portraits"],
  ["13", "Portraits"],
];

const prKitImages = imageDetails
  .map(([imageNumber, category], index) => {
    const number = String(index + 1).padStart(2, "0");
    const fileName = `harisharan-devgan-pr-kit-${number}.png`;

    return {
      id: `pr-kit-${number}`,
      thumbnail: `/images/pr-kit/thumbnails/harisharan-devgan-pr-kit-${number}.webp`,
      original: `/images/pr-kit/${fileName}`,
      titleKey: `prKitPage.images.${imageNumber}.title`,
      category,
      downloadName: fileName,
    };
  })
  .map((image, index) => ({
    ...image,
    id: `pr-kit-${String(index + 1).padStart(2, "0")}`,
  }));

const categories = ["All"];

const getImageUrl = (src) => new URL(src, window.location.origin).href;

const copyToClipboard = async (text) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const input = document.createElement("textarea");
  input.value = text;
  input.style.position = "fixed";
  input.style.opacity = "0";
  document.body.appendChild(input);
  input.select();
  document.execCommand("copy");
  input.remove();
};

function MediaCard({ image, index, onOpen, onToast, t }) {
  const [failed, setFailed] = useState(false);

  const handleCopy = async (event) => {
    event.stopPropagation();
    try {
      await copyToClipboard(getImageUrl(image.original));
      onToast(t("prKitPage.toast.originalLinkCopied"));
    } catch {
      onToast(t("prKitPage.toast.copyUnavailable"));
    }
  };

  return (
    <article className="pr-kit-card" style={{ "--pr-card-delay": `${Math.min(index * 0.045, 0.35)}s` }}>
      <figure className="pr-kit-card-figure">
        <button type="button" className="pr-kit-card-button" onClick={() => onOpen(image.id)} aria-label={`${t("prKitPage.actions.view")} ${image.title}`}>
          {failed ? <span className="pr-kit-card-error"><ImageOff size={24} />{t("prKitPage.imageUnavailable")}</span> : <img src={image.thumbnail} alt={image.alt} loading="lazy" decoding="async" onError={() => setFailed(true)} />}
          <span className="pr-kit-card-overlay"><span>{t("prKitPage.actions.view")}</span><ExternalLink size={16} /></span>
        </button>
        <figcaption className="pr-kit-card-caption">
          <span className="pr-kit-category-badge">{t("prKitPage.category")}</span>
          <h3>{image.title}</h3>
          <div className="pr-kit-card-actions">
            <button type="button" onClick={() => onOpen(image.id)}><ExternalLink size={14} />{t("prKitPage.actions.view")}</button>
            <a href={image.original} download={image.downloadName} onClick={(event) => event.stopPropagation()}><Download size={14} />{t("prKitPage.actions.download")}</a>
            <button type="button" className="pr-kit-card-copy" onClick={handleCopy} aria-label={`${t("prKitPage.actions.copyLink")} ${image.title}`} title={t("prKitPage.actions.copyLink")}><LinkIcon size={14} /></button>
          </div>
        </figcaption>
      </figure>
    </article>
  );
}

function ImageLightbox({ image, index, total, onClose, onNavigate, onToast, t }) {
  const closeButtonRef = useRef(null);
  const [failed, setFailed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dimensions, setDimensions] = useState(null);

  useEffect(() => {
    closeButtonRef.current?.focus();
  }, [image.id]);

  const copyLink = async () => {
    try {
      await copyToClipboard(getImageUrl(image.original));
      onToast(t("prKitPage.toast.originalLinkCopied"));
    } catch {
      onToast(t("prKitPage.toast.copyUnavailable"));
    }
  };

  const shareImage = async () => {
    const url = getImageUrl(image.original);
    if (navigator.share) {
      try {
        await navigator.share({ title: image.title, text: "Harisharan Devgan media asset", url });
        onToast(t("prKitPage.toast.linkShared"));
        return;
      } catch (error) {
        if (error.name === "AbortError") return;
      }
    }
    try {
      await copyToClipboard(url);
      onToast(t("prKitPage.toast.originalLinkCopied"));
    } catch {
      onToast(t("prKitPage.toast.copyUnavailable"));
    }
  };

  return (
    <div className="pr-kit-lightbox" role="dialog" aria-modal="true" aria-label={t("prKitPage.mediaViewer")}>
      <div className="pr-kit-lightbox-topbar">
        <span className="pr-kit-counter">{index + 1} / {total}</span>
        <div className="pr-kit-lightbox-actions">
          <a className="pr-kit-icon-action" href={image.original} target="_blank" rel="noopener noreferrer" aria-label={t("prKitPage.actions.openOriginal")} title={t("prKitPage.actions.openOriginal")}><ExternalLink size={18} /><span>{t("prKitPage.actions.openOriginal")}</span></a>
          <a className="pr-kit-icon-action" href={image.original} download={image.downloadName} aria-label={t("prKitPage.actions.downloadOriginal")} title={t("prKitPage.actions.downloadOriginal")}><Download size={18} /><span>{t("prKitPage.actions.downloadOriginal")}</span></a>
          <button className="pr-kit-icon-action" type="button" onClick={copyLink} aria-label={t("prKitPage.actions.copyOriginalLink")} title={t("prKitPage.actions.copyOriginalLink")}><LinkIcon size={18} /><span>{t("prKitPage.actions.copyOriginalLink")}</span></button>
          <button className="pr-kit-icon-action" type="button" onClick={shareImage} aria-label={t("prKitPage.actions.share")} title={t("prKitPage.actions.share")}><Share2 size={18} /><span>{t("prKitPage.actions.share")}</span></button>
          <button ref={closeButtonRef} className="pr-kit-close" type="button" onClick={onClose} aria-label={t("prKitPage.actions.close")} title={t("prKitPage.actions.close")}><X size={22} /></button>
        </div>
      </div>

      <div className="pr-kit-lightbox-stage">
        <button className="pr-kit-nav pr-kit-nav-prev" type="button" onClick={() => onNavigate(-1)} disabled={index === 0} aria-label={t("prKitPage.actions.previous")} title={t("prKitPage.actions.previous")}><ChevronLeft size={32} /></button>
        <div className="pr-kit-viewer-image-wrap" aria-busy={loading}>
          {failed ? <div className="pr-kit-viewer-error"><ImageOff size={36} /><span>{t("prKitPage.imageUnavailable")}</span></div> : <><img src={image.original} alt={image.alt} onLoad={(event) => { setDimensions({ width: event.currentTarget.naturalWidth, height: event.currentTarget.naturalHeight }); setLoading(false); }} onError={() => { setFailed(true); setLoading(false); }} />{loading && <div className="pr-kit-viewer-loading" role="status"><span className="pr-kit-spinner" />{t("prKitPage.loadingOriginal")}</div>}</>}
        </div>
        <button className="pr-kit-nav pr-kit-nav-next" type="button" onClick={() => onNavigate(1)} disabled={index === total - 1} aria-label={t("prKitPage.actions.next")} title={t("prKitPage.actions.next")}><ChevronRight size={32} /></button>
      </div>

      <div className="pr-kit-lightbox-footer">
        <div><span>{t("prKitPage.category")}</span><h2>{image.title}</h2><dl className="pr-kit-metadata"><div><dt>{t("prKitPage.metadata.file")}</dt><dd>PNG</dd></div>{dimensions && <div><dt>{t("prKitPage.metadata.resolution")}</dt><dd>{dimensions.width} x {dimensions.height}</dd></div>}<div><dt>{t("prKitPage.metadata.usage")}</dt><dd>{t("prKitPage.metadata.editorial")}</dd></div><div><dt>{t("prKitPage.metadata.credit")}</dt><dd>Harisharan Devgan</dd></div></dl></div>
        <div className="pr-kit-mobile-nav"><button type="button" onClick={() => onNavigate(-1)} disabled={index === 0}><ArrowLeft size={16} />{t("prKitPage.actions.previous")}</button><button type="button" onClick={() => onNavigate(1)} disabled={index === total - 1}>{t("prKitPage.actions.next")}<ArrowRight size={16} /></button></div>
      </div>
    </div>
  );
}

export default function PRKit() {

  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeCategory, setActiveCategory] = useState("All");
  const [toast, setToast] = useState("");
  const galleryRef = useRef(null);
  const selectedId = searchParams.get("image");
  const localizedImages = prKitImages.map((image) => ({ ...image, title: `Harisharan Devgan - ${t(image.titleKey)}`, alt: `Harisharan Devgan - ${t(image.titleKey).toLowerCase()}` }));
  const filteredImages = activeCategory === "All" ? localizedImages : localizedImages.filter((image) => image.category === activeCategory);
  const selectedImage = localizedImages.find((image) => image.id === selectedId);
  const selectedIndex = selectedImage ? localizedImages.findIndex((image) => image.id === selectedImage.id) : -1;

  useEffect(() => {
    if (!selectedImage && selectedId) setSearchParams({}, { replace: true });
  }, [selectedId, selectedImage, setSearchParams]);

  useEffect(() => {
    if (!selectedImage) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setSearchParams({}, { replace: true });
      if (event.key === "ArrowLeft" && selectedIndex > 0) setSearchParams({ image: localizedImages[selectedIndex - 1].id }, { replace: true });
      if (event.key === "ArrowRight" && selectedIndex < localizedImages.length - 1) setSearchParams({ image: localizedImages[selectedIndex + 1].id }, { replace: true });
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [selectedImage, selectedIndex, setSearchParams]);

  useEffect(() => {
    if (!toast) return undefined;
    const timeout = window.setTimeout(() => setToast(""), 2600);
    return () => window.clearTimeout(timeout);
  }, [toast]);

  const openImage = (id) => setSearchParams({ image: id });
  const navigateImage = (direction) => {
    const nextIndex = selectedIndex + direction;
    if (nextIndex >= 0 && nextIndex < localizedImages.length) openImage(localizedImages[nextIndex].id);
  };

  const siteUrl = (import.meta.env.VITE_SITE_URL || "https://harisharandevgan.com").replace(/\/$/, "");
  const canonicalUrl = `${siteUrl}/pr-kit/`;
  const socialImage = `${siteUrl}/images/news-banner.webp`;
  const gallerySchema = {
    "@context": "https://schema.org",
    "@type": "ImageGallery",
    name: "Harisharan Devgan PR Kit",
    description: t("prKitPage.meta.description"),
    url: canonicalUrl,
    author: { "@type": "Person", name: "Harisharan Devgan", url: siteUrl },
    image: localizedImages.map((image) => ({ "@type": "ImageObject", contentUrl: `${siteUrl}${image.original}`, thumbnailUrl: `${siteUrl}${image.thumbnail}`, name: image.title, caption: image.alt })),
  };

  return (
    <div className="pr-kit-page">
      <Helmet>
        <title>Harisharan Devgan Media Kit | Official Photos &amp; Resources</title>
        <meta name="description" content="Access the official media kit of Harisharan Devgan, including high-resolution photographs, portraits and resources for journalists, publications and media professionals." />
        <meta name="keywords" content="Harisharan Devgan PR kit, Harisharan Devgan photos, press photos, media resources, agriculture photography" />
        <meta name="author" content="Harisharan Devgan" />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <meta name="theme-color" content="#171615" />
        <link rel="canonical" href={canonicalUrl} />
        <link rel="icon" href="/favicon/favicon.ico" />
        <link rel="icon" type="image/png" sizes="32x32" href="/favicon/favicon-32x32.png" />
        <link rel="icon" type="image/png" sizes="16x16" href="/favicon/favicon-16x16.png" />
        <link rel="apple-touch-icon" sizes="180x180" href="/favicon/apple-touch-icon.png" />
        <link rel="manifest" href="/favicon/site.webmanifest" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content="Harisharan Devgan Media Kit | Official Photos &amp; Resources" />
        <meta property="og:description" content="Access the official media kit of Harisharan Devgan, including high-resolution photographs, portraits and resources for journalists, publications and media professionals." />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Harisharan Devgan" />
        <meta property="og:locale" content="en_IN" />
        <meta property="og:image" content={socialImage} />
        <meta property="og:image:secure_url" content={socialImage} />
        <meta property="og:image:type" content="image/webp" />
        <meta property="og:image:width" content="1920" />
        <meta property="og:image:height" content="600" />
        <meta property="og:image:alt" content={t("prKitPage.meta.imageAlt")} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Harisharan Devgan Media Kit | Official Photos &amp; Resources" />
        <meta name="twitter:description" content="Access the official media kit of Harisharan Devgan, including high-resolution photographs, portraits and resources for journalists, publications and media professionals." />
        <meta name="twitter:image" content={socialImage} />
        <meta name="twitter:image:alt" content={t("prKitPage.meta.imageAlt")} />
        <script type="application/ld+json">{JSON.stringify(gallerySchema)}</script>
      </Helmet>

      <section className="relative min-h-[400px] flex items-center mt-[98px] overflow-hidden">
        <img
          src="/images/news-clips.webp"
          alt={t("prKitPage.bannerAlt")}
          loading="eager"
          fetchPriority="high"
          decoding="async"
          width="1920"
          height="600"
          className="page-banner-image page-banner-image--pr-kit absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/70"></div>

        <div className="relative max-w-7xl mx-auto w-full px-6 grid grid-cols-1 md:grid-cols-2 items-center gap-8">
          <div>
            <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-3 uppercase tracking-wider">
             {t("prKitPage.title")}
            </h1>
            <p className="text-lg text-gray-300">
             {t("prKitPage.subtitle")}
            </p>
          </div>

          <div className="md:text-right">
            <h2 className="text-lg font-semibold text-white">
              <Link to="/" className="text-gray-300 hover:text-orange-500 transition">
                {t("common.home")}
              </Link>
              <span className="mx-2 text-gray-400">/</span>
              <span className="text-orange-500">{t("prKitPage.title")}</span>
            </h2>
          </div>
        </div>
      </section>

      <section ref={galleryRef} className="pr-kit-gallery-section" aria-labelledby="gallery-heading">
        <div className="pr-kit-section-head"><div><span className="pr-kit-kicker">{t("prKitPage.forPress")}</span><h2 id="gallery-heading">{t("prKitPage.collection")}</h2><p>{t("prKitPage.collectionDescription")}</p></div><span className="pr-kit-count">{filteredImages.length} {filteredImages.length === 1 ? t("prKitPage.asset") : t("prKitPage.assets")}</span></div>
        <div className="pr-kit-filters" role="tablist" aria-label={t("prKitPage.filterLabel")}>
          {categories.map((category) => <button key={category} type="button" role="tab" aria-selected={activeCategory === category} aria-controls="pr-kit-gallery" className={activeCategory === category ? "is-active" : ""} onClick={() => setActiveCategory(category)}>{t("prKitPage.all")}</button>)}
        </div>
        {filteredImages.length ? <div id="pr-kit-gallery" className="pr-kit-masonry">{filteredImages.map((image, index) => <MediaCard key={image.id} image={image} index={index} onOpen={openImage} onToast={setToast} t={t} />)}</div> : <div className="pr-kit-empty">{t("prKitPage.empty")}</div>}
      </section>

      <section className="pr-kit-info-band" aria-labelledby="media-use-heading">
        <div className="pr-kit-info-inner"><div><span className="pr-kit-kicker">{t("prKitPage.mediaUse")}</span><h2 id="media-use-heading">{t("prKitPage.attribution")}</h2></div><div className="pr-kit-info-copy"><p>{t("prKitPage.mediaUseDescription")}</p><p>{t("prKitPage.commercialDescription")}</p></div></div>
      </section>

      <section className="pr-kit-contact-band" aria-labelledby="media-inquiries-heading">
        <div className="pr-kit-contact-inner"><span className="pr-kit-kicker">{t("prKitPage.inquiries")}</span><h2 id="media-inquiries-heading">{t("prKitPage.needSpecific")}</h2><p>{t("prKitPage.contactDescription")}</p><p>{t("prKitPage.contactAssistance")}</p><div className="pr-kit-contact-actions"><Link className="pr-kit-primary-action" to="/connect/">{t("prKitPage.contactMedia")} <ArrowRight size={17} /></Link><Link className="pr-kit-secondary-action" to="/connect/">{t("prKitPage.visitContact")}</Link></div></div>
      </section>

      {selectedImage && <ImageLightbox key={selectedImage.id} image={selectedImage} index={selectedIndex} total={localizedImages.length} onClose={() => setSearchParams({}, { replace: true })} onNavigate={navigateImage} onToast={setToast} t={t} />}
      <div className="pr-kit-toast" role="status" aria-live="polite" aria-atomic="true" data-visible={Boolean(toast)}><Check size={16} />{toast}</div>
    </div>
  );
}